import { CoreButtonView, type TCoreButtonViewProps } from '@core/widgets';
import { ConditionalRender } from '@libs/core';
import { AssetsSvgMap, TAssetsSvgMapValue, TButtonData, TWidget } from '@libs/types';
import React, { MouseEvent } from 'react';
import { SemanticColor, semanticColors } from '../../theme/colors';
import { TButtonOption } from '../../types/options';
import { TVisualViewProps, VisualView } from '../Image/VisualView';
import { ButtonCircleGlassView } from './ButtonCircleGlassView';
import {
    getCircleBg,
    getCircleBgDisabled,
    getCircleHoverBg,
    getCircleIconColor,
    getCircleIconColorDisabled,
    getCirclePressedBg,
} from './styles/buttonCircleUtils';

const SIZE_CONFIG: Record<string, { circleSize: number; iconSize: number }> = {
    small: { circleSize: 28, iconSize: 16 },
    medium: { circleSize: 40, iconSize: 24 },
    large: { circleSize: 56, iconSize: 24 },
};

export type TButtonCircleViewProps = {
    iconName?: TAssetsSvgMapValue;
    label?: string;
    position?: 'floating' | 'inline';
    size?: 'small' | 'medium' | 'large';
    boxShadow?: boolean;
    onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
    // Note: onClick is optional here to support cases where no click handler is needed,
    // but CoreButtonView requires a non-optional handler — we supply a no-op fallback.
    isDisabled?: boolean;
    isHide?: boolean;
    iconWidth: number;
    iconHeight: number;
    raised?: boolean;
    hierarchy?: TButtonOption['hierarchy'];
    margin?: TButtonOption['margin'];
    parentStyleNames?: string[];
    /** Used solely for theme resolution via useThemeHook inside CoreButtonView. Optional. */
    widget?: TWidget<TButtonData, TButtonOption>;
    iconColor?: TVisualViewProps['iconColor'];
    backgroundColor?: TVisualViewProps['backgroundColor'];
};

export const getIconHierarchy = (hierarchy: string) => {
    switch (hierarchy) {
        case 'highlighted':
            return 'highlighted';
        case 'primary':
            return 'emphasised';
        case 'secondary':
            return undefined;
        case 'tertiary':
            return 'primary';
        case 'destructive':
            return 'destructive';
        default:
            return 'emphasised';
    }
};

/**
 * Composable view for the circle-shaped button used across consumer screens.
 * Owns only rendering logic — accepts all behavior props as pre-resolved inputs
 * from the parent (isDisabled, isHide, onClick, raised).
 *
 * Use this component when behavior state is already managed one level up,
 * e.g. inside a complex composed widget or a screen-level handler.
 * For server-driven usage, prefer ButtonCircleWidget which wires up all hooks.
 */
export const ButtonCircleView = ({
    iconName,
    label,
    size = 'medium',
    hierarchy = 'tertiary',
    margin,
    isDisabled,
    isHide,
    raised,
    boxShadow = true,
    iconWidth,
    iconHeight,
    parentStyleNames,
    widget,
    onClick,
    iconColor,
    backgroundColor,
}: TButtonCircleViewProps) => {
    const icon = AssetsSvgMap[iconName as keyof typeof AssetsSvgMap] ?? 'ic_x';
    const sizeConfig = SIZE_CONFIG[size ?? 'medium'] ?? SIZE_CONFIG.large;
    // If concrete icon dimensions are provided, derive circleSize from them using the same
    // circle-to-icon proportion encoded in SIZE_CONFIG; otherwise fall back to the fixed value.
    const hasIconDimensions = !!iconWidth && !!iconHeight;
    const circleSize = hasIconDimensions ? Math.round(Math.max(iconWidth, iconHeight) * (sizeConfig.circleSize / sizeConfig.iconSize)) : sizeConfig.circleSize;
    const paddingX = Math.max(0, Math.floor((circleSize - iconWidth) / 2));
    const paddingY = Math.max(0, Math.floor((circleSize - iconHeight) / 2));

    const resolvedWidget: TCoreButtonViewProps['widget'] = (widget ?? { uid: '', data: {}, options: {} }) as TCoreButtonViewProps['widget'];

    // const iconVariant = hierarchy === 'primary' ? 'circle' : 'simple';
    const iconSize = size === 'small' ? 'xxSmall' : undefined;

    if (hierarchy === 'tertiaryGlass') {
        return (
            <ButtonCircleGlassView
                uid={widget?.uid}
                widget={widget}
                circleSize={circleSize}
                iconName={icon}
                iconWidth={iconWidth}
                iconHeight={iconHeight}
                iconSize={iconSize}
                iconHierarchy={getIconHierarchy(hierarchy)}
                iconColor={iconColor}
                backgroundColor={backgroundColor}
                label={label}
                hierarchy={hierarchy}
                margin={margin}
                isDisabled={isDisabled}
                isHide={isHide}
                parentStyleNames={parentStyleNames}
                onClick={onClick}
            />
        );
    }

    return (
        <CoreButtonView
            widget={resolvedWidget}
            isDisabled={isDisabled}
            isHide={isHide}
            parentStyleNames={parentStyleNames}
            text={label}
            styleName="buttonCircle"
            styleProps={{
                main: getCircleBg(hierarchy),
                outline: semanticColors[SemanticColor.BorderEmphasised],
                borderRadius: circleSize / 2,
                borderStroke: hierarchy !== 'tertiary' ? 4 : 0,
                height: circleSize,
                width: circleSize,
                padding: { top: paddingY, right: paddingX, bottom: paddingY, left: paddingX },
                margin,
                boxShadow: raised && boxShadow ? 'var(--shadow_grey)' : undefined,
                cursorDisabled: 'default',
                disabled: {
                    main: getCircleBgDisabled(hierarchy),
                },
                hovered: {
                    main: getCircleHoverBg(hierarchy),
                    outline: semanticColors[SemanticColor.BorderEmphasised],
                },
                pressed: {
                    main: getCirclePressedBg(hierarchy),
                },
            }}
            onClick={onClick ?? (() => undefined)}
        >
            <ConditionalRender condition={!!icon}>
                <VisualView
                    isDisabled={isDisabled}
                    parentStyleNames={parentStyleNames}
                    styleName="buttonCircleIcon"
                    defaultIconName={AssetsSvgMap.ic_x}
                    size={iconSize}
                    iconWidth={iconWidth}
                    iconHeight={iconHeight}
                    iconName={icon}
                    iconHierarchy={getIconHierarchy(hierarchy)}
                    iconVariant={'simple'}
                    iconColor={iconColor ? iconColor : isDisabled ? getCircleIconColorDisabled(hierarchy) : getCircleIconColor(hierarchy)}
                    backgroundColor={backgroundColor}
                />
            </ConditionalRender>
        </CoreButtonView>
    );
};
